import PropTypes from 'prop-types'
import React from 'react'

import StripePaymentBlock from 'components/_special/StripePaymentBlock'
import ButtonAsyncAction from 'components/ButtonAsyncAction'
import { JP } from 'constants/countries'
import { useCollectWithChecks } from 'hooks/cart/collectDataStep'
import { useDropdownOptions } from 'hooks/cart/useDropdownOptions'
import { useInitialPersonalInfoValues } from 'hooks/cart/useInitialPersonalInfoValues'
import { usePersonalInfoForm } from 'hooks/cart/usePersonalnfoForm'

import OrderPersInfoEuForm from './components/OrderPersInfoEuForm'
import OrderPersInfoJapanForm from './components/OrderPersInfoJapanForm'
import { ButtonWrapper } from './styles'

const PERSONAL_INFO_FORM_ID = 'form-personal-info'

/**
 * Component of CartFormEurope
 *
 * > Here we got form for European region and some stuff to work with Stripe
 * > We are verify value of agreement checkbox only inside this component
 */
const CartFormEurope = ({
  dict,
  texts,
  rules,
  locale,
  cart,
  countries,
  sales,

  personalInfoChange,
  handleFailure,
  handleSuccess,

  handleCollect,
  handleCollectReset,
  handlePayment,
}) => {
  const { isFetching, deliveryType, isOrderCollected } = cart
  const piFormRef = React.useRef(null)
  const prevLocale = React.useRef(null)

  /*
   * Options for dropdowns
   */
  const {
    defaultCountryValue,
    defaultPhoneCodeValue,
    optionsCountry,
    optionsPhoneCode,
  } = useDropdownOptions({ countries, dict, locale })

  /*
   * Object with initial values for form
   * > be careful to change this object it will cause to form reload (loop danger)
   */
  const personalInfoInitialValues = useInitialPersonalInfoValues({
    personal: prevLocale.current !== locale ? {} : cart.personal,
    defaultCountryValue,
    defaultPhoneCodeValue,
  })

  React.useEffect(() => {
    if (prevLocale.current !== locale) prevLocale.current = locale
  }, [locale])

  /*
   * Personal Info form
   * > save to state validity status for form
   * > function to handle changes
   */
  const [
    isPersonalInfoReady,
    errors,
    handlePersonalInfoChange,
  ] = usePersonalInfoForm({
    personalInfoChange,
  })
  /*
   * remote personal info form submit
   */
  const remotePersonalInfoSubmit = React.useCallback(
    () =>
      document
        .getElementById(PERSONAL_INFO_FORM_ID)
        .dispatchEvent(new Event('submit', { cancelable: true })),
    [],
  )

  /*
   * Callback
   * > Order info Collect handler
   */
  const handleCollectWithChecks = useCollectWithChecks({
    isPersonalInfoReady,
    errors,
    remotePersonalInfoSubmit,
    handleCollect,
    isOrderCollected,
    isFetching,
  })

  const orderPersInfoProps = {
    key: `info-${locale}`,
    ref: piFormRef,
    texts: texts.form,
    id: PERSONAL_INFO_FORM_ID,
    onChange: handlePersonalInfoChange,
    onSubmit: handleCollectWithChecks,
    errorsDict: dict.errors,
    optionsCountry: optionsCountry,
    optionsPhoneCode: optionsPhoneCode,
    initialValues: personalInfoInitialValues,
    isDisabled: isFetching || isOrderCollected,
    deliveryType: deliveryType,
  }

  return (
    <>
      {locale === JP ? (
        <OrderPersInfoJapanForm {...orderPersInfoProps} rules={rules} />
      ) : (
        <OrderPersInfoEuForm
          {...orderPersInfoProps}
          isSpecialPromotion={sales.isGroupon}
        />
      )}

      {!isOrderCollected && (
        <ButtonWrapper>
          <ButtonAsyncAction
            onClick={handleCollectWithChecks}
            isLoading={isFetching}
            animAlign="center"
          >
            {sales.isGroupon
              ? dict.buttons.checkout_without_pay
              : dict.buttons.checkout}
          </ButtonAsyncAction>
        </ButtonWrapper>
      )}

      {isOrderCollected && !sales.isGroupon && cart.isSinglePage && (
        <StripePaymentBlock
          handleCollectWithChecks={handleCollectWithChecks}
          cart={cart}
          handleCollectReset={handleCollectReset}
          handleSuccess={handleSuccess}
          texts={texts}
          handlePayment={handlePayment}
          handleFailure={handleFailure}
          sales={sales}
        />
      )}
    </>
  )
}

CartFormEurope.propTypes = {
  cart: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  dict: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  texts: PropTypes.object.isRequired,
  rules: PropTypes.object,
  personalInfoChange: PropTypes.func.isRequired,
  handleFailure: PropTypes.func.isRequired,
  handleSuccess: PropTypes.func.isRequired,
  handleCollect: PropTypes.func.isRequired,
  handleCollectReset: PropTypes.func.isRequired,
  handlePayment: PropTypes.func.isRequired,
  sales: PropTypes.object.isRequired,
}

export default CartFormEurope
